import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

let globalClose = null;

const TooltipOverlay = ({ text, content, onClose, triggerRef }) => {
  const contentRef = useRef(null);
  const [desktopPos, setDesktopPos] = useState(null);
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const body = content || text;

  useEffect(() => {
    if (!isMobile && triggerRef.current && contentRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = contentRef.current.getBoundingClientRect();
      const tooltipW = 300;
      const tooltipH = tooltipRect.height || 200;
      const margin = 12;
      const gap = 10;

      // Horizontal: center on trigger, clamp to viewport edges
      let left = triggerRect.left + triggerRect.width / 2 - tooltipW / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - tooltipW - margin));

      // Vertical: prefer above, fall back to below
      const spaceAbove = triggerRect.top - margin;
      const spaceBelow = window.innerHeight - triggerRect.bottom - margin;

      if (spaceAbove >= tooltipH + gap) {
        // Show above
        setDesktopPos({ left, bottom: window.innerHeight - triggerRect.top + gap });
      } else if (spaceBelow >= tooltipH + gap) {
        // Show below
        setDesktopPos({ left, top: triggerRect.bottom + gap });
      } else {
        // Not enough room either way — show below and let it scroll, clamped to viewport
        const top = Math.max(margin, Math.min(triggerRect.bottom + gap, window.innerHeight - tooltipH - margin));
        setDesktopPos({ left, top });
      }
    }
  }, [isMobile, triggerRef]);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile]);

  const onBackdrop = (e) => {
    if (contentRef.current && !contentRef.current.contains(e.target)) onClose();
  };

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-6"
        onClick={onBackdrop}
        data-testid="info-tooltip-overlay"
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-tooltip-backdrop" />
        <div
          ref={contentRef}
          className="relative z-10 w-[88vw] max-w-sm px-5 py-5 rounded-2xl bg-card border border-border/60 shadow-2xl text-[14px] leading-relaxed text-foreground-secondary animate-tooltip-modal max-h-[80dvh] overflow-y-auto"
          style={{ scrollbarWidth: 'thin' }}
          data-testid="info-tooltip-content"
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100]"
      onClick={onBackdrop}
      data-testid="info-tooltip-overlay"
    >
      <div
        ref={contentRef}
        className="fixed px-5 py-4 rounded-xl bg-card border border-border shadow-2xl text-[13px] leading-relaxed text-foreground-secondary animate-tooltip-desktop max-h-[70dvh] overflow-y-auto whitespace-pre-line"
        style={{ width: 300, ...(desktopPos || { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }), scrollbarWidth: 'thin' }}
        data-testid="info-tooltip-content"
      >
        {body}
      </div>
    </div>
  );
};

const InfoTooltip = ({ text, content, className = '' }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    if (globalClose && globalClose !== close) globalClose();
    globalClose = close;

    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (globalClose === close) globalClose = null;
    };
  }, [open, close]);

  const handleTrigger = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex items-center align-middle ${className}`}
        onClick={handleTrigger}
        role="button"
        aria-label="Nánari upplýsingar"
        data-testid="info-tooltip-trigger"
      >
        <Info
          size={17}
          className="text-foreground-muted/60 hover:text-accent cursor-pointer transition-colors"
          strokeWidth={2.2}
        />
      </span>
      {open && createPortal(
        <TooltipOverlay text={text} content={content} onClose={close} triggerRef={triggerRef} />,
        document.body
      )}
    </>
  );
};

export default InfoTooltip;
